import './ConfirmationModal.scss';

import React, { useEffect, useRef, useState } from 'react';
import { FlightButton, FlightTextInput, getIcon } from '@flybits/design-system';
import LoadingIcon from 'components/Shared/LoadingIcon/LoadingIcon';
import { ConfirmationModalActionStyles, ConfirmationModalProps, ConfirmationModalTypes } from '../shared.types';

const CLASS_MAIN = 'confirmation-modal';
const CLASSES = {
  DELETE_MODAL: `${CLASS_MAIN}--delete`,
  LAUNCH_MODAL: `${CLASS_MAIN}--launch`,
  WARNING_MODAL: `${CLASS_MAIN}--warning`,
  CHILDREN: `${CLASS_MAIN}__children`,
  CLOSE_ICON: `${CLASS_MAIN}__children__close-icon`,
  BODY: `${CLASS_MAIN}__children__body`,
  ICON: `${CLASS_MAIN}__children__body__icon`,
  LAUNCH_ICON: `${CLASS_MAIN}__children__body__icon--launch`,
  CONTAINER: `${CLASS_MAIN}__children__body__container`,
  INFO: `${CLASS_MAIN}__children__body__container__info`,
  TITLE: `${CLASS_MAIN}__children__body__container__info__title`,
  DESCRIPTION: `${CLASS_MAIN}__children__body__container__info__description`,
  DELETE_MODAL_DESCRIPTION: `${CLASS_MAIN}__children__body__container__info__description--delete`,
  BANNER: `${CLASS_MAIN}__children__body__container__banner`,
  RED_BANNER: `${CLASS_MAIN}__children__body__container__banner--red`,
  AMBER_BANNER: `${CLASS_MAIN}__children__body__container__banner--amber`,
  BANNER_ICON: `${CLASS_MAIN}__children__body__container__banner__icon`,
  BANNER_INFO: `${CLASS_MAIN}__children__body__container__banner__info`,
  BANNER_TITLE: `${CLASS_MAIN}__children__body__container__banner__info__title`,
  BANNER_DESC: `${CLASS_MAIN}__children__body__container__banner__info__desc`,
  CONFIRMATION_INPUT: `${CLASS_MAIN}__children__body__container__confirmation-input`,
  CONFIRMATION_LABEL: `${CLASS_MAIN}__children__body__container__confirmation-input__label`,
  ACTIONS: `${CLASS_MAIN}__children__body__container__actions`,
  DELETE_ACTIONS: `${CLASS_MAIN}__children__body__container__actions--delete`,
  LAUNCH_ACTIONS: `${CLASS_MAIN}__children__body__container__actions--launch`,
  COLUMN_ACTIONS: `${CLASS_MAIN}__children__body__container__actions--column`,
  ACTIONS_BUTTON: `${CLASS_MAIN}__children__body__container__actions__button`,
  PRIMARY_WITH_BADGE: `${CLASS_MAIN}__children__body__container__actions__button--primary-with-badge`,
  PRIMARY_BADGE: `${CLASS_MAIN}__children__body__container__actions__primary-badge`,
  LOADING_CONTAINER: `${CLASS_MAIN}__loading-container`,
  LOADING_SPINNER: `${CLASS_MAIN}__loading-container__spinner`,
};

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  closeModal,
  theme,
  icon,
  title,
  description,
  primaryAction,
  secondaryAction,
  isLoading,
  confirmationInput: {
    placeholderText: confirmationPlaceholderText,
    matchText: confirmationMatchText,
    label: confirmationInputLabel = '',
  } = {},
  className = '',
  actionStyle = '',
  warningMessage = '',
  infoBanner: {
    icon: infoBannerIcon = getIcon('infoFilled', {}),
    title: infoBannerTitle,
    description: infoBannerDescription,
  } = {},
  primaryActionBadge,
  useSecondaryLinkStyle = false,
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [confirmInputVal, setConfirmInputVal] = useState('');
  const [confirmDisabled, setConfirmDisabled] = useState(true);

  useEffect(() => {
    if (isOpen) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <dialog
      ref={dialogRef}
      onCancel={closeModal}
      className={`${CLASS_MAIN} ${
        theme === ConfirmationModalTypes.DELETE
          ? CLASSES.DELETE_MODAL
          : theme === ConfirmationModalTypes.LAUNCH
          ? CLASSES.LAUNCH_MODAL
          : theme === ConfirmationModalTypes.WARNING
          ? CLASSES.WARNING_MODAL
          : ''
      } ${className}`}
    >
      {isLoading && (
        <div className={CLASSES.LOADING_CONTAINER}>
          <div className={CLASSES.LOADING_SPINNER}>
            <LoadingIcon />
          </div>
        </div>
      )}
      <div className={CLASSES.CHILDREN}>
        <div className={CLASSES.CLOSE_ICON}>
          <button onClick={closeModal}>{getIcon('clear', {})}</button>
        </div>
        <div className={CLASSES.BODY}>
          <div className={`${CLASSES.ICON} ${theme === ConfirmationModalTypes.LAUNCH ? CLASSES.LAUNCH_ICON : ''}`}>
            {icon}
          </div>
          <div className={CLASSES.CONTAINER}>
            <div className={CLASSES.INFO}>
              <div className={CLASSES.TITLE}>{title}</div>
              <div
                className={`${CLASSES.DESCRIPTION} ${
                  theme === ConfirmationModalTypes.DELETE ? CLASSES.DELETE_MODAL_DESCRIPTION : ''
                }`}
              >
                {description}
              </div>
            </div>
            {infoBannerTitle && (
              <div className={`${CLASSES.BANNER} ${CLASSES.AMBER_BANNER}`}>
                <div className={CLASSES.BANNER_ICON}>{infoBannerIcon}</div>
                <div className={CLASSES.BANNER_INFO}>
                  <div className={CLASSES.BANNER_TITLE}>{infoBannerTitle}</div>
                  <div className={CLASSES.BANNER_DESC}>{infoBannerDescription}</div>
                </div>
              </div>
            )}
            {warningMessage && (
              <div className={`${CLASSES.BANNER} ${CLASSES.RED_BANNER}`}>
                <div className={`${CLASSES.BANNER_ICON} ${CLASSES.BANNER_ICON}--red`}>{getIcon('warning', {})}</div>
                <div className={CLASSES.BANNER_INFO}>
                  <div className={`${CLASSES.BANNER_TITLE} ${CLASSES.BANNER_TITLE}--red`}>Warning</div>
                  <div className={CLASSES.BANNER_DESC}>{warningMessage}</div>
                </div>
              </div>
            )}
            {confirmationMatchText && (
              <div className={CLASSES.CONFIRMATION_INPUT}>
                {confirmationInputLabel && <div className={CLASSES.CONFIRMATION_LABEL}>{confirmationInputLabel}</div>}
                <FlightTextInput
                  name="merchant name"
                  width="100%"
                  value={confirmInputVal}
                  placeholderText={confirmationPlaceholderText || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const inputVal = e.target.value || '';
                    setConfirmInputVal(inputVal);
                    setConfirmDisabled(inputVal !== confirmationMatchText);
                  }}
                />
              </div>
            )}
            {theme === ConfirmationModalTypes.DELETE ? (
              <div
                className={`${CLASSES.ACTIONS} ${CLASSES.DELETE_ACTIONS} ${
                  actionStyle === ConfirmationModalActionStyles.COLUMN ? CLASSES.COLUMN_ACTIONS : ''
                }`}
              >
                <div className={`${CLASSES.ACTIONS_BUTTON} ${CLASSES.ACTIONS_BUTTON}--secondary`}>
                  <FlightButton
                    label={secondaryAction.value}
                    ariaLabel={secondaryAction.value}
                    onClick={secondaryAction.onClickHandler ?? (() => null)}
                    theme={'minor'}
                  />
                </div>
                <div className={`${CLASSES.ACTIONS_BUTTON} ${CLASSES.ACTIONS_BUTTON}--delete`}>
                  <FlightButton
                    label={primaryAction.value}
                    ariaLabel={primaryAction.value}
                    onClick={primaryAction.onClickHandler ?? (() => null)}
                    theme={'primary'}
                    disabled={!!confirmationMatchText && confirmDisabled}
                  />
                </div>
              </div>
            ) : theme === ConfirmationModalTypes.WARNING ? (
              <div className={`${CLASSES.ACTIONS} ${CLASSES.ACTIONS}--warning`}>
                <div className={`${CLASSES.ACTIONS_BUTTON} ${CLASSES.ACTIONS_BUTTON}--secondary`}>
                  <FlightButton
                    label={secondaryAction.value}
                    ariaLabel={secondaryAction.value}
                    onClick={secondaryAction.onClickHandler}
                    theme={'minor'}
                  />
                </div>
                <div className={`${CLASSES.ACTIONS_BUTTON} ${CLASSES.ACTIONS_BUTTON}--primary-warning`}>
                  <FlightButton
                    label={primaryAction.value}
                    ariaLabel={primaryAction.value}
                    onClick={primaryAction.onClickHandler}
                    theme={'primary'}
                  />
                </div>
              </div>
            ) : (
              <div
                className={`${CLASSES.ACTIONS} ${
                  theme === ConfirmationModalTypes.LAUNCH ? CLASSES.LAUNCH_ACTIONS : ''
                }`}
              >
                <div
                  className={`${CLASSES.ACTIONS_BUTTON} ${CLASSES.ACTIONS_BUTTON}${
                    theme !== ConfirmationModalTypes.LAUNCH ? '--primary' : ''
                  } ${primaryActionBadge ? CLASSES.PRIMARY_WITH_BADGE : ''}`}
                >
                  <FlightButton
                    label={primaryAction.value}
                    ariaLabel={primaryAction.value}
                    onClick={primaryAction.onClickHandler ?? (() => null)}
                    theme={'primary'}
                    disabled={!!confirmationMatchText && confirmDisabled}
                  />
                  {primaryActionBadge && <span className={CLASSES.PRIMARY_BADGE}>{primaryActionBadge}</span>}
                </div>
                <div className={`${CLASSES.ACTIONS_BUTTON} ${CLASSES.ACTIONS_BUTTON}--secondary`}>
                  <FlightButton
                    label={secondaryAction.value}
                    ariaLabel={secondaryAction.value}
                    onClick={secondaryAction.onClickHandler ?? (() => null)}
                    theme={useSecondaryLinkStyle ? 'link' : 'minor'}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </dialog>
  );
};

export default ConfirmationModal;
